import { createDisclosure } from "./SiteHeader.disclosure";
import { createPanel } from "./SiteHeader.panel";
import { EBar, EPanel, getHeaderStore, takePanel } from "./SiteHeader.store";

const MENU = "[data-fa-account-menu]";
const TOGGLE = "[data-fa-account-menu-toggle]";
const PANEL = "[data-fa-account-menu-panel]";

/** Returns its restore, which the header's teardown owns — listeners ride the caller's signal. */
export const initAccount = (root: HTMLElement, signal: AbortSignal): (() => void) => {
	const menu = root.querySelector<HTMLElement>(MENU);
	const toggle = menu?.querySelector<HTMLElement>(TOGGLE);
	const dropdown = menu?.querySelector<HTMLElement>(PANEL);

	if (!menu || !toggle || !dropdown) return () => undefined;

	const store = getHeaderStore(root);
	const isOpen = () => toggle.getAttribute("aria-expanded") === "true";

	const panel = createPanel({
		key: EPanel.ACCOUNT,
		root,
		settles: dropdown,
		returnTo: () => toggle,
		isOpen,
		release: () => {
			if (dropdown.contains(document.activeElement)) toggle.focus({ preventScroll: true });
			toggle.setAttribute("aria-expanded", "false");
			dropdown.inert = true;
		},
	});

	const close = panel.close;

	/* `panel.hold()` is deliberately not called: the field gives mutual exclusion with the mega,
	   drawer and brands, and this one alone leaves the page scrollable and live. */
	const open = () => {
		void takePanel(store, EPanel.ACCOUNT, {
			claim: () => {
				toggle.setAttribute("aria-expanded", "true");
				dropdown.inert = false;
			},
			stillWanted: isOpen,
		});
	};

	const options = { signal };

	toggle.addEventListener(
		"click",
		() => {
			/* `false`: the click already focused the toggle — see SiteHeader.drawer.ts. */
			if (isOpen()) close(false);
			else open();
		},
		options,
	);

	/* The disclosure's focusout cannot see this one: a press on inert page furniture blurs to
	   `<body>` with a `null` relatedTarget, which is indistinguishable from the window leaving. */
	document.addEventListener(
		"pointerdown",
		(event) => {
			if (!isOpen() || (event.target instanceof Node && menu.contains(event.target))) return;

			close(false);
		},
		options,
	);

	createDisclosure({ scope: menu, contains: dropdown, isOpen, close, signal });

	/* Nothing else needs this, because every other panel locks the page: without the hold the
	   user keeps scrolling, and past the collapse threshold the utility band goes
	   `visibility: hidden` with the dropdown inside it — open, unreachable, still holding the
	   field. */
	const unfollowBar = store.subscribe(() => {
		if (store.read().bar === EBar.COLLAPSED && isOpen()) close(false);
	});

	return () => {
		unfollowBar();
		panel.dispose();
	};
};
